// @react-native-picker/picker stand-in: a native <select> with the same
// `Picker` / `Picker.Item` API and `selectedValue` / `onValueChange` props.

import React from 'react';

export interface PickerItemProps<T = string> {
  label: string;
  value: T;
  color?: string;
  enabled?: boolean;
}

export interface PickerProps<T = string> {
  selectedValue?: T;
  onValueChange?: (value: T, index: number) => void;
  enabled?: boolean;
  style?: unknown;
  itemStyle?: unknown;
  mode?: 'dialog' | 'dropdown';
  prompt?: string;
  accessibilityLabel?: string;
  testID?: string;
  children?: React.ReactNode;
}

function PickerItem<T>(_props: PickerItemProps<T>): null {
  return null;
}

export function Picker<T extends string | number = string>(props: PickerProps<T>): React.JSX.Element {
  const items: PickerItemProps<T>[] = [];
  React.Children.forEach(props.children, (child) => {
    if (React.isValidElement<PickerItemProps<T>>(child)) items.push(child.props);
  });
  const selectedIndex = Math.max(
    0,
    items.findIndex((i) => i.value === props.selectedValue),
  );

  return (
    <select
      value={String(props.selectedValue ?? items[0]?.value ?? '')}
      disabled={props.enabled === false}
      aria-label={props.accessibilityLabel ?? props.prompt}
      data-testid={props.testID}
      onChange={(e) => {
        const idx = e.target.selectedIndex;
        const item = items[idx];
        if (item) props.onValueChange?.(item.value, idx);
      }}
      style={selectStyle}
      defaultValue={undefined}
    >
      {items.map((item, i) => (
        <option key={`${String(item.value)}-${i}`} value={String(item.value)} disabled={item.enabled === false}>
          {item.label}
        </option>
      ))}
      {items.length === 0 ? <option value="">{selectedIndex >= 0 ? '' : ''}</option> : null}
    </select>
  );
}

Picker.Item = PickerItem;

const selectStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 48,
  fontSize: 16,
  color: '#2C4364',
  background: '#FFFFFF',
  border: '1px solid #E5E9F0',
  borderRadius: 50,
  padding: '0 18px',
  fontFamily: 'inherit',
};

export default Picker;
